import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  getAdminMerchantsGetAdminMerchantsIdGuidQueryKey,
  getAdminMerchantsGetAdminMerchantsQueryKey,
  useAdminMerchantsGetAdminMerchantsIdGuid,
  useAdminMerchantsPostAdminMerchantsIdGuidActivate,
  useAdminMerchantsPostAdminMerchantsIdGuidSuspend,
} from '@/lib/api/admin-merchants/admin-merchants';
import { useAdminStoresDeleteAdminStoresIdGuid } from '@/lib/api/admin-stores/admin-stores';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useHasRole } from '@/lib/auth/useAuth';
import { AdyenCredentialsCard } from './AdyenCredentialsCard';
import { EditMerchantDialog } from './EditMerchantDialog';
import { AdminCreateStoreDialog } from './AdminCreateStoreDialog';

/**
 * Admin hierarchy level 2: /merchants/:id shows merchant detail + its stores.
 * Clicking a store could drill further, but stores are today leaf nodes —
 * edits happen inline via dialogs.
 */
export default function AdminMerchantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canMutate = useHasRole('Admin');
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [editOpen, setEditOpen] = useState(false);
  const [createStoreOpen, setCreateStoreOpen] = useState(false);

  const merchantQuery = useAdminMerchantsGetAdminMerchantsIdGuid(id ?? '', { query: { enabled: !!id } });

  const suspend = useAdminMerchantsPostAdminMerchantsIdGuidSuspend();
  const activate = useAdminMerchantsPostAdminMerchantsIdGuidActivate();
  const deleteStore = useAdminStoresDeleteAdminStoresIdGuid();

  const toggle = async () => {
    if (!id || !merchantQuery.data) return;
    try {
      if (merchantQuery.data.isActive) await suspend.mutateAsync({ id });
      else await activate.mutateAsync({ id });
      await queryClient.invalidateQueries({ queryKey: getAdminMerchantsGetAdminMerchantsIdGuidQueryKey(id) });
      await queryClient.invalidateQueries({ queryKey: getAdminMerchantsGetAdminMerchantsQueryKey() });
    } catch {
      toast.error('Could not change merchant status.');
    }
  };

  const removeStore = async (storeId: string, storeLabel: string) => {
    if (!id) return;
    const ok = await confirm({
      title: 'Delete this store?',
      description: (
        <>
          <strong className="font-semibold">{storeLabel}</strong> will be removed from the merchant.
          Past transactions audited against this store stay intact, but the store reference can no
          longer be used on new requests. This cannot be undone.
        </>
      ),
      confirmLabel: 'Delete store',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await deleteStore.mutateAsync({ id: storeId });
      toast.success('Store deleted.');
      // Refresh the merchant detail (which carries the stores list) and any
      // open merchant lists so counts update too.
      await queryClient.invalidateQueries({ queryKey: getAdminMerchantsGetAdminMerchantsIdGuidQueryKey(id) });
      await queryClient.invalidateQueries({ queryKey: getAdminMerchantsGetAdminMerchantsQueryKey() });
    } catch {
      toast.error('Could not delete store.');
    }
  };

  if (!id) return <div className="text-sm text-destructive">Missing merchant id.</div>;

  if (merchantQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading merchant…</div>;
  }
  if (!merchantQuery.data) {
    return (
      <div className="text-sm text-destructive">
        Merchant not found.{' '}
        <Link to="/merchants" className="font-medium text-primary hover:underline">
          Back to list
        </Link>
      </div>
    );
  }

  const m = merchantQuery.data;

  return (
    <section className="space-y-5">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" className="gap-2" onClick={() => navigate(`/clients/${m.clientId}`)}>
          <ArrowLeft className="h-4 w-4" />
          Back to client
        </Button>
        <span className="text-xs text-muted-foreground">·</span>
        <Link to="/merchants" className="text-xs text-muted-foreground hover:text-foreground">
          All merchants
        </Link>
      </div>

      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{m.name}</h1>
          <p className="font-mono text-xs text-muted-foreground">{m.merchantId}</p>
        </div>
        {canMutate && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={toggle} disabled={suspend.isPending || activate.isPending}>
              {m.isActive ? 'Suspend' : 'Activate'}
            </Button>
            <Button variant="outline" className="gap-2" onClick={() => setEditOpen(true)}>
              <Pencil className="h-4 w-4" /> Edit merchant
            </Button>
          </div>
        )}
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Status</CardDescription>
            <CardTitle>
              {m.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="warning">Suspended</Badge>}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Balance Account</CardDescription>
            <CardTitle className="text-sm font-mono">{m.balanceAccountId || '—'}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Created</CardDescription>
            <CardTitle className="text-sm font-normal">{new Date(m.createdAt).toLocaleDateString()}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <AdyenCredentialsCard
        merchantId={id}
        adyenApiKeySet={m.adyenApiKeySet ?? false}
        adyenLiveUrlPrefix={m.adyenLiveUrlPrefix}
      />

      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle>Stores</CardTitle>
            <CardDescription>Physical or logical sales locations under this merchant.</CardDescription>
          </div>
          {canMutate && (
            <Button size="sm" className="gap-2" onClick={() => setCreateStoreOpen(true)}>
              <Plus className="h-4 w-4" /> Add store
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {!m.stores || m.stores.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No stores yet.{canMutate && ' Add one to start routing per-store.'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-3 font-medium">Name</th>
                  <th className="p-3 font-medium">Store ID</th>
                  <th className="p-3 font-medium">Status</th>
                  {canMutate && <th className="p-3 font-medium text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {m.stores.map((s) => (
                  <tr key={s.id} className="hover:bg-muted/30">
                    <td className="p-3 font-medium">{s.name}</td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{s.storeId}</td>
                    <td className="p-3">
                      {s.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="warning">Suspended</Badge>}
                    </td>
                    {canMutate && (
                      <td className="p-3 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="gap-1 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => removeStore(s.id, s.name)}
                          disabled={deleteStore.isPending}
                        >
                          <Trash2 className="h-4 w-4" /> Delete
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {canMutate && (
        <EditMerchantDialog merchantId={editOpen ? id : null} onOpenChange={(open) => !open && setEditOpen(false)} />
      )}
      {canMutate && (
        <AdminCreateStoreDialog
          merchantId={createStoreOpen ? id : null}
          onOpenChange={(open) => !open && setCreateStoreOpen(false)}
        />
      )}
    </section>
  );
}
